import { useListCirclesQuery } from "@/api/circles-api";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Circle } from "@/types/circle";
import { isRTLLanguage } from "@/utils/helpers";
import { Link } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import StepCard from "../challenge/cards/StepCard";
import CircleCard from "./cards/CircleCard";
import { useTour } from "@/hooks/useTour";
import { useEffect } from "react";

const CirclesTab = () => {
  const { t, i18n } = useTranslation();
  const { startTour, getTourStatus, restoreTourIfIncomplete, tourState } = useTour();

  useEffect(() => {
    if (tourState.isActive || tourState.tourType !== "circles-page") {
      return;
    }
    const tourStatus = getTourStatus("circles-page");

    restoreTourIfIncomplete("circles-page");
    if (tourStatus === "not-started") {
      // Start a new tour if user has never seen it
      startTour("circles-page", 3);
    }
    // Do nothing if tour is "completed" or "skipped"
  }, [startTour, getTourStatus, restoreTourIfIncomplete]);

  const { data: efbCircles, isLoading: efbCirclesLoading } = useListCirclesQuery({
    is_owned_by_efb_directly: true,
    is_joined: false,
  });
  const { data: joinedCircles, isLoading: joinedCirclesLoading } = useListCirclesQuery({
    is_joined: true,
  });
  const joinedCirclesCount = joinedCircles?.data.count ?? 0;

  return (
    <div dir={isRTLLanguage(i18n.language) ? "rtl" : "ltr"} className="min-h-screen">
      <div className="flex flex-col gap-6 p-6 lg:flex-row  lg:justify-between">
        <div className="flex flex-col gap-12 ">
          <h3 className="text-xl font-bold leading-[120%] sm:text-5xl">
            {t("circle.manageCircles")}
          </h3>
          <p className="-mt-10 md:-mt-8 md:w-4/6 ">{t("circle.description")} </p>
          {joinedCirclesCount > 0 && (
            <div className="-mt-10 rounded-lg bg-[#E2DDD7] p-6 md:-mt-8 md:w-4/6">
              <p>{t("circle.selectCircle")}</p>
            </div>
          )}
        </div>
        <div>
          <Link aria-label="Go to create challenge page" to="/circle-creation">
            <Button variant="bold" size="lg" className="gap-2" id="joyride-create-circle">
              <span>{t("circle.createCircle")}</span>
              <PlusIcon />
            </Button>
          </Link>
        </div>
      </div>
      {efbCirclesLoading && joinedCirclesLoading && (
        <p className="px-4 py-6 text-center text-lg">{t("circle.loading")}</p>
      )}
      {!efbCirclesLoading && !joinedCirclesLoading && joinedCirclesCount === 0 ? (
        <div className="flex gap-6 overflow-x-auto overflow-y-hidden ">
          <ScrollArea className="w-full">
            <div className="flex shrink-0 flex-row gap-6 overflow-x-auto">
              <div className="flex h-full shrink-0 flex-row gap-2">
                {efbCircles?.data.results.map((item: Circle) => (
                  <CircleCard circle={item} key={item.id} />
                ))}
              </div>
              <StepCard title={t("circle.step1")} logo="Number=1.svg" step="1/3" />
              <StepCard title={t("circle.step2")} logo="Number=2.svg" step="2/3" />
              <StepCard title={t("circle.step3")} logo="Number=3.svg" step="3/3" />
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      ) : (
        <div className="w-full p-6">
          <ScrollArea className="w-full pb-2">
            <div
              className="flex h-full shrink-0 flex-row gap-6 overflow-x-auto"
              id="joyride-circle-cards"
            >
              {efbCircles?.data.results.map((item: Circle) => (
                <CircleCard circle={item} key={item.id} />
              ))}
              {joinedCircles?.data.results.map((item: Circle, idx: number) => (
                <CircleCard
                  circle={item}
                  key={item.id}
                  id={idx === 0 ? "joyride-circle-card" : undefined} // Add id for the first card
                />
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default CirclesTab;
